import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LOGO_HORIZONTAL } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { PLATFORM, platformLogin } from "@/lib/platform";
import { Loader2, Lock, Mail } from "lucide-react";

export default function AdminLogin() {
  const { user, login, loading, platformError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(platformError || "");
  const nav = useNavigate();

  if (user) return <Navigate to="/admin" replace />;
  if (PLATFORM && !platformError) {
    platformLogin(); // sign-in is the platform's (a failed hand-over stays here, with its error)
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const res = await login(email, password);
    if (res.ok) nav("/admin");
    else setError(res.error);
  };

  return (
    <div
      data-testid="login-page"
      className="flex min-h-screen items-center justify-center bg-heda-cream px-4"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-3xl border border-heda-line bg-white p-7 shadow-heda sm:p-8"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO_HORIZONTAL} alt="Heda Plywoods" className="h-20 w-auto" />
          <h1 className="mt-4 font-display text-2xl font-bold text-heda-ink">Sign in to your studio</h1>
          <p className="mt-1 text-[13px] text-heda-mute">
            Manage products, QR codes and AI assistants for Heda Plywoods.
          </p>
        </div>

        <label className="heda-label" htmlFor="email">Email</label>
        <div className="relative mb-4">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            id="email"
            type="email"
            data-testid="login-email-input"
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="heda-input pl-9"
            placeholder="you@hedaplywoods.com"
          />
        </div>

        <label className="heda-label" htmlFor="password">Password</label>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            id="password"
            type="password"
            data-testid="login-password-input"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="heda-input pl-9"
            placeholder="••••••••"
          />
        </div>

        {error && (
          <div data-testid="login-error-message" className="heda-error mt-3">
            {error}
          </div>
        )}

        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={loading}
          className="heda-btn mt-5 w-full"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
          Enter Studio
        </button>

        <p className="mt-5 text-center text-[11.5px] text-heda-mute">Hedaply · AI Product Studio</p>
      </form>
    </div>
  );
}
